import React, { useEffect } from "react";
import { Box, Typography, Button, styled, keyframes } from "@mui/material";
import { useTranslation } from "react-i18next";
import DietitianAppointment from "../../pages/DietitianAppointment";

// Animation keyframes
const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const textSlideUp = keyframes`
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface NotSureWhereToStartSectionProps {
  leftContentProps?: {
    isVisible?: boolean;
    enableTextAnimation?: boolean;
    delay?: number;
  };
  rightContentProps?: {
    isVisible?: boolean;
    enableAnimation?: boolean;
    delay?: number;
  };
}

// Styled components for animations
const AnimatedLeftContent = styled(Box, {
  shouldForwardProp: (prop) => !["isVisible", "enableTextAnimation", "delay"].includes(prop as string),
})<{
  isVisible: boolean;
  enableTextAnimation: boolean;
  delay: number;
}>(({ isVisible, enableTextAnimation, delay }) => ({
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? "translateX(0)" : "translateX(-30px)",
  animation: isVisible ? `${slideInFromLeft} 0.8s ease-out ${delay}s both` : "none",
  transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
  willChange: "transform, opacity",

  // Text animations for left content
  ...(enableTextAnimation &&
    isVisible && {
      "& h1, & h2, & h3, & h4, & h5, & h6": {
        animation: `${textSlideUp} 1s ease-out ${delay + 0.3}s both`,
      },
      "& p": {
        animation: `${textSlideUp} 1s ease-out ${delay + 0.5}s both`,
      },
    }),
}));

const AnimatedRightContent = styled(Box, {
  shouldForwardProp: (prop) => !["isVisible", "enableAnimation", "delay"].includes(prop as string),
})<{
  isVisible: boolean;
  enableAnimation: boolean;
  delay: number;
}>(({ isVisible, enableAnimation, delay }) => ({
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? "translateX(0)" : "translateX(30px)",
  animation:
    isVisible && enableAnimation
      ? `${slideInFromRight} 0.8s ease-out ${delay}s both`
      : isVisible
      ? `${fadeInUp} 0.6s ease-out ${delay}s both`
      : "none",
  transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
  willChange: "transform, opacity",

  // Explicitly disable text animations for form content
  "& *": {
    animation: "none !important",
  },
}));

const NotSureWhereToStartSection: React.FC<NotSureWhereToStartSectionProps> = ({
  leftContentProps = { isVisible: true, enableTextAnimation: false, delay: 0 },
  rightContentProps = { isVisible: true, enableAnimation: false, delay: 0 },
}) => {
  const { t, i18n } = useTranslation();

  return (
    <Box
      sx={{
        position: "relative",
        background: "#ffffff",
        borderRadius: { xs: "16px", sm: "24px", md: "32px", lg: "48px" },
        border: { xs: "none", md: "1px solid #e2ede6" },
        boxShadow: { xs: "none", md: "0px 2px 0px 0px #e2ede6" },
        width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)", md: "100%" },
        maxWidth: "1440px",
        minHeight: { xs: "auto", md: "800px" },
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "0 auto",
        py: { xs: 3, sm: 4 },
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          left: { xs: "-50px", sm: "-30px", md: "-24px" },
          top: { xs: "-200px", sm: "-300px", md: "-444px" },
          width: { xs: "800px", sm: "1100px", md: "1475px" },
          height: { xs: "800px", sm: "1100px", md: "1475px" },
          backgroundImage: `url("/images/f47d60afe319b742acb701c1b439605a7589c20c.svg")`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          zIndex: 0,
          display: { xs: "none", sm: "block" },
        }}
      />

      {/* Content Container */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          gap: { xs: "20px", sm: "32px", md: "60px", lg: "80px" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "center",
          width: "100%",
          maxWidth: { xs: "100%", sm: "600px", md: "900px", lg: "1110px" },
          minHeight: { xs: "auto", md: "390px" },
          flexDirection: { xs: "column", md: "row" },
          px: { xs: 1.5, sm: 2.5, md: 4, lg: 0 },
          py: { xs: 1, sm: 2, md: 0 },
        }}
      >
        {/* Left Content */}
        <AnimatedLeftContent
          isVisible={leftContentProps.isVisible ?? true}
          enableTextAnimation={leftContentProps.enableTextAnimation ?? false}
          delay={leftContentProps.delay ?? 0}
          sx={{
            flex: { xs: "none", md: 1 },
            display: "flex",
            flexDirection: "column",
            gap: { xs: "12px", sm: "16px", md: "20px", lg: "24px" },
            alignItems: { xs: "center", md: "flex-start" },
            justifyContent: { xs: "center", md: "flex-start" },
            textAlign: { xs: "center", md: i18n.language === "ar" ? "right" : "left" },
            color: "#3e4044",
            width: { xs: "100%", md: "auto" },
            px: { xs: 1, sm: 0 },
          }}
        >
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: { xs: "20px", sm: "22px", md: "24px", lg: "28px" },
              lineHeight: 1.5,
              color: "#3e4044",
              mb: 0,
              maxWidth: { xs: "100%", md: "none" },
            }}
          >
            {t('common.notSureWhereToStart.title')}
            <br />
            {t('common.notSureWhereToStart.subtitle')}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: { xs: "14px", sm: "15px", md: "16px", lg: "16px" },
              lineHeight: 1.5,
              color: "#3e4044",
              maxWidth: { xs: "100%", md: "none" },
            }}
          >
            {t('common.notSureWhereToStart.description')}
          </Typography>
        </AnimatedLeftContent>

        {/* Form Card */}
        <AnimatedRightContent
          isVisible={rightContentProps.isVisible ?? true}
          enableAnimation={rightContentProps.enableAnimation ?? false}
          delay={rightContentProps.delay ?? 0}
          sx={{
            flex: { xs: "none", md: 1 },
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "center",
            width: { xs: "100%", md: "auto" },
          }}
        >
          <Box
            sx={{
              background: "#ffffff",
              borderRadius: { xs: "12px", sm: "16px" },
              border: { xs: "1px solid #cfd1d5", sm: "1px solid #eff0f0" },
              boxShadow: { xs: "0px 1px 0px 0px #cfd1d5", sm: "0px 2px 0px 0px #eff0f0" },
              width: "100%",
              maxWidth: { xs: "100%", sm: "450px", md: "490px" },
              minHeight: { xs: "auto", md: "390px" },
              padding: { xs: "16px", sm: "20px", md: "24px" },
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "center",
              mx: { xs: 0.5, sm: 0 },
            }}
          >
            <DietitianAppointment showTitle={false} />
          </Box>
        </AnimatedRightContent>
      </Box>
    </Box>
  );
};

export default NotSureWhereToStartSection;
